import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { ThemeApplierService } from '../../../theme/theme-applier.service';
import { APP_CONFIG } from '@cardapio-online/config';
import { PublicHeroStateService, PublicHeroPromoItem } from '../hero/public-hero-state.service';

/** Rota principal do catálogo (showcase). */
const SHOWCASE_PATH = '/cardapio';

/** Rotas que ocultam o header e hero (foco total do cliente). */
const FOCUS_ROUTE_PREFIXES = ['/checkout', '/orders/'];

@Component({
  selector: 'lib-shell-layout',
  templateUrl: './shell-layout.component.html',
  styleUrl: './shell-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellLayoutComponent implements OnInit {
  private readonly theme = inject(ThemeApplierService);
  private readonly router = inject(Router);
  private readonly heroState = inject(PublicHeroStateService);

  protected readonly cfg = inject(APP_CONFIG);

  /** True quando o usuário scrollou além da altura do hero. */
  protected readonly scrolledPastThreshold = signal(false);

  /**
   * Shell-header compacto aparece APENAS quando o hero foi scrollado
   * para fora da viewport — não há dependência de rota.
   */
  protected readonly compactHeaderVisible = this.scrolledPastThreshold.asReadonly();

  /** True quando a rota exige foco total (checkout, confirmação). */
  protected readonly focusRouteActive = signal(false);

  protected readonly heroPromos = this.heroState.promos;
  protected readonly heroBannerUrl = this.cfg.heroBannerUrl ?? null;

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((e) => {
        this.scrolledPastThreshold.set(false);
        this.syncFocusRoute(e.urlAfterRedirects);
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'instant' });
        }
      });
  }

  ngOnInit(): void {
    this.theme.apply();
    this.syncScrollState();
    this.syncFocusRoute(this.router.url);
  }

  @HostListener('window:scroll')
  protected onWindowScroll(): void {
    this.syncScrollState();
  }

  protected openPromo(promo: PublicHeroPromoItem): void {
    void this.router.navigate(['/item', promo.id]);
  }

  protected onSearch(term: string): void {
    this.heroState.setSearchTerm(term);

    const currentPath = this.router.url.split('?')[0];

    if (currentPath !== SHOWCASE_PATH) {
      void this.router.navigateByUrl(SHOWCASE_PATH);
      return;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private syncFocusRoute(url: string): void {
    this.focusRouteActive.set(
      FOCUS_ROUTE_PREFIXES.some((prefix) => url.startsWith(prefix)),
    );
  }

  private syncScrollState(): void {
    if (typeof window === 'undefined') {
      this.scrolledPastThreshold.set(false);
      return;
    }

    // Shell-header aparece após o hero ter saído completamente da viewport.
    this.scrolledPastThreshold.set(window.scrollY > 360);
  }
}
